/**
 * Teacher-pause grace helpers for the student assessment player (ASM-749).
 *
 * Enforcement lives on the item-save response (`pauseState`, see edu-api
 * TestActivity.getPauseStateForSave). There is no client-held pause state, no anchor and no realtime
 * (LLD D2/D4) — every decision here is made from one save response plus the `testItemId` that save
 * carried.
 *
 * Deliberately no clock arithmetic: the server owns the grace window.
 *
 * ASM-806/812: `ejectForPause` used to lazily `require('../../configureStore')` and call
 * `history.push()` on that module's `history` directly, to dodge a real import cycle
 * (`author/ExpressGrader/ducks` -> `assessment/sagas/items` -> this file -> `configureStore` ->
 * root reducer -> every duck, incl. ExpressGrader's, back again). But `../../configureStore` is a
 * hardcoded path to the MAIN bundle's store module (`src/index.js` / `App.js`). The student bundle
 * (`src/studentEntry.js` / `AppStudent.js`) is wired to a SEPARATE `history` singleton exported by
 * `configureStoreStudents.js` (its own `createBrowserHistory()` call). Pushing on the wrong,
 * unconnected `history` instance still calls the browser's real `pushState` (so the URL bar
 * genuinely changes), but never notifies the listener the real, rendered `<ConnectedRouter>` is
 * subscribed to, so React never re-renders — the assessment player stayed mounted while the URL
 * showed `/home/assignments`.
 *
 * Fix: go through `window.getStore().dispatch(push(...))` instead. `window.getStore` is the
 * existing bundle-agnostic escape hatch (both `configureStore.js` and `configureStoreStudents.js`
 * set it, from whichever one the running entry point actually invoked), and `routerMiddleware`
 * inside that same store forwards the `push` action to the store's OWN, correctly-wired history —
 * the exact mechanism the saga's own normal-path navigation already relies on
 * (`assessment/sagas/items.js`, `yield put(push(...))`). No `configureStore*` import needed at all,
 * so the cycle above can't recur.
 */
import { notification } from '@edulastic/common'
import { push } from 'connected-react-router'

export const PAUSE_NOTIFICATION_KEY = 'studentAssignmentPaused'
export const PAUSED_REDIRECT_URL = '/home/assignments'

/** itemId currently in the address bar, or null outside the player. */
export const currentItemIdFromUrl = () =>
  window.location.pathname.match(/\/itemId\/([^/?#]+)/)?.[1] || null

/**
 * True while the address bar still points inside the assessment player. Every player route carries
 * `/uta/<testActivityId>`, including the non-item ones (`test-summary`, `sections-start`), while the
 * paused redirect targets (`/home/assignments`, `/home/playlist/:id`) do not.
 */
export const isInPlayer = () => window.location.pathname.includes('/uta/')

/**
 * True when `urlToGo` leaves the assessment player: Save & Exit, playlist return, SEB quit (none of
 * those carry `/uta/`), OR a final Submit landing on the test-summary review page
 * (`/uta/:utaId/test-summary`) — the attempt is genuinely over even though that URL still has
 * `/uta/` in it.
 *
 * The section-boundary variant (`/uta/:utaId/section/:sectionId/test-summary`, submitted from the
 * last item of a non-final section) is deliberately NOT included: more sections remain, so the
 * ASM-749 LLD's D9 decision still applies and that submission keeps ejecting (ASM-813).
 */
export const isPlayerExitNavigation = (urlToGo) => {
  const url = `${urlToGo}`
  if (!urlToGo) return false
  if (!url.includes('/uta/')) return true
  return /\/uta\/[^/]+\/test-summary$/.test(url)
}

/**
 * Leave the player because the class was paused.
 *
 * The `paused` sessionStorage flag is REQUIRED, not decoration. `useFullScreenListener`'s cleanup fires
 * 5s after unmount and, on restrict-navigation tests, calls `pauseAssignment` -> `togglePauseStudents`
 * (`themes/index.js` fullscreen cleanup, helper above it), which would apply a *student-level* pause
 * with an "Anti Cheating measures" toast and leave this student paused after the teacher resumes the
 * class. The flag is the existing suppression idiom in that file (the multi-login path).
 */
export const ejectForPause = ({ isPlaylist = false } = {}) => {
  window.sessionStorage.setItem('paused', 1)
  setTimeout(() => window.sessionStorage.removeItem('paused'), 10000)
  notification({
    messageKey: PAUSE_NOTIFICATION_KEY,
    key: PAUSE_NOTIFICATION_KEY,
  })
  window
    .getStore()
    .dispatch(
      push(
        isPlaylist
          ? `/home/playlist/${isPlaylist?.playlistId}`
          : PAUSED_REDIRECT_URL
      )
    )
}

/**
 * Attached to the save promise, so it still runs when `throttleAction` cancelled the saga that issued
 * the save (LLD §3.2, last row). Ejects only when the student has already MOVED — i.e. the URL no
 * longer shows the item this save was for. Sitting still is left alone (D6), and on the normal path
 * this runs before the saga resumes and sees the unchanged URL, so the saga's own gate does the
 * ejecting and there is no double toast.
 */
export const ejectIfMovedDuringPause = (
  saveResponse,
  savedTestItemId,
  { isPlaylist = false } = {}
) => {
  if (!saveResponse?.pauseState?.isPaused) return
  if (!savedTestItemId) return
  /**
   * Bail out once we are no longer in the player, or the timeout handler already landed on an
   * allowed exit (Save & Exit, or a final Submit's test-summary — ASM-813): either this handler
   * already ejected the student, or they left through an already-permitted route. Re-pushing here
   * would double-toast.
   */
  if (!isInPlayer() || isPlayerExitNavigation(window.location.pathname)) return
  const urlItemId = currentItemIdFromUrl()
  /**
   * Still in the player with no itemId means the timeout handler navigated to a non-item destination
   * that is NOT a final Submit — e.g. a section-boundary Submit's section-summary page. That push
   * carried no pause gate, so eject — D9 wants those to eject like any other navigation, since more
   * sections remain. Save & Exit and the final test-summary are handled by the
   * `isPlayerExitNavigation` check above.
   */
  if (urlItemId && `${urlItemId}` === `${savedTestItemId}`) return
  ejectForPause({ isPlaylist })
}
